/**
 * The calendar's date arithmetic, hand-rolled (ADR-026: no date library). Everything
 * works on ISO calendar dates - "YYYY-MM-DD", the shape every API date already
 * arrives in - and on month indexes 0-11 as the grid layout needs.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

/** One grid cell: the ISO date it stands for and whether it belongs to the month. */
export interface GridCell {
  readonly iso: string;
  readonly year: number;
  /** Month index 0-11. */
  readonly month: number;
  readonly day: number;
  readonly inMonth: boolean;
}

const MS_PER_DAY = 86_400_000;

/** Parse "YYYY-MM-DD" (or an ISO timestamp) into its parts. Throws on anything else. */
export function parseISO(iso: string): { year: number; month: number; day: number } {
  const date = iso.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (match === null) {
    throw new Error(`Expected an ISO calendar date, read ${iso}`);
  }
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
}

/** Format the parts back to "YYYY-MM-DD". */
export function toISO(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${String(year)}-${mm}-${dd}`;
}

/** The UTC-midnight instant of an ISO date; date math stays in whole days. */
function utcMidnight(year: number, month: number, day: number): number {
  return Date.UTC(year, month, day);
}

/** Whole days from `from` to `to`, both "YYYY-MM-DD". */
export function daysBetween(from: string, to: string): number {
  const a = parseISO(from);
  const b = parseISO(to);
  return Math.round(
    (utcMidnight(b.year, b.month, b.day) - utcMidnight(a.year, a.month, a.day)) / MS_PER_DAY,
  );
}

/** The ISO date `days` whole days after `iso`. */
export function addDays(iso: string, days: number): string {
  const { year, month, day } = parseISO(iso);
  const shifted = new Date(utcMidnight(year, month, day) + days * MS_PER_DAY);
  return toISO(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate());
}

/** Monday-first weekday of an ISO date: 0 for Monday through 6 for Sunday. */
export function weekdayIndex(iso: string): number {
  const { year, month, day } = parseISO(iso);
  return (new Date(utcMidnight(year, month, day)).getUTCDay() + 6) % 7;
}

/**
 * The month grid: whole weeks, Monday first, so the first and last rows carry the
 * neighbouring months' days (`inMonth` false). Always a multiple of 7 cells.
 */
export function monthCells(year: number, month: number): GridCell[] {
  const first = toISO(year, month, 1);
  const lead = weekdayIndex(first);
  const gridStart = addDays(first, -lead);
  const cells: GridCell[] = [];
  for (let index = 0; index < 42; index += 1) {
    const iso = addDays(gridStart, index);
    const parts = parseISO(iso);
    cells.push({ ...parts, iso, inMonth: parts.month === month && parts.year === year });
  }
  // Trim the trailing whole week when the month fits in five.
  if (cells[35]?.inMonth === false) {
    return cells.slice(0, 35);
  }
  return cells;
}

/**
 * The quarter grid: three month columns, one row per possible day 1..31, and null
 * where the month has no such day.
 */
export function quarterColumns(year: number, quarter: 1 | 2 | 3 | 4): (GridCell | null)[][] {
  const firstMonth = (quarter - 1) * 3;
  const columns: (GridCell | null)[][] = [];
  for (let offset = 0; offset < 3; offset += 1) {
    const month = firstMonth + offset;
    const length = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const column: (GridCell | null)[] = [];
    for (let day = 1; day <= 31; day += 1) {
      if (day > length) {
        column.push(null);
      } else {
        column.push({ iso: toISO(year, month, day), year, month, day, inMonth: true });
      }
    }
    columns.push(column);
  }
  return columns;
}
